<template>
  <div class="score score--dapsa rythm-v">
    <h4>Score DAPSA<br /><small>(Disease Activity in PSoriatic Arthritis)</small></h4>

    <div>
      <radio-toggle class="radio-group--inline" id="score_dapsa_done" name="score_dapsa_done" label="Score réalisé ?" v-model="scoreDone" @input="resetScore"></radio-toggle>
    </div>

    <div v-if="hasScore" class="rythm-v-large">
      <section class="rythm-v">
        <h5>Articulations douloureuses</h5>
        <div class="rythm-v">
          <p>
            Nombre d'articulations douloureuses : <strong>{{ painfulJointsTotal }}</strong>
          </p>
          <DasSelect :key="`dapsa_painfuljoints_${session.id}`" set="dapsa" v-model="painfulJoints.checkedDots" @input="emitScore"></DasSelect>
        </div>
      </section>

      <section class="rythm-v">
        <h5>Articulations gonflées</h5>
        <div class="rythm-v">
          <p>
            Nombre d'articulations gonflées : <strong>{{ swollenJointsTotal }}</strong>
          </p>
          <DasSelect :key="`dapsa_swollenjoints_${session.id}`" set="dapsa" v-model="swollenJoints.checkedDots" @input="emitScore"></DasSelect>
        </div>
      </section>

      <!-- CRP -->
      <div class="flex-row flex-gap-large">
        <div class="flex-item--50">
          <radio-toggle class="radio-group--inline" id="score_dapsa_crp_done" name="score_dapsa_crp_done" label="CRP réalisée ?" v-model="crpDone" @input="resetCrp"></radio-toggle>
          <div class="input-group--inline required" v-if="parseInt(crpDone)">
            <label for="dapsa_crp">CRP (mg/l) :</label>
            <input type="number" name="dapsa_crp" id="dapsa_crp" v-model="crp" v-bind="$attrs" step=".01" required @input="emitScore" />
          </div>
        </div>
      </div>

      <!-- EVA Index -->
      <section class="rythm-v">
        <h5>EVA (DAPSA)</h5>

        <div class="eva rythm-v-large">
          <div>
            En tenant compte des conséquences de votre rhumatisme psoriasique sur votre vie quotidienne, conjugale, familiale et sociale, comment évalueriez-vous l’activité de votre rhumatisme durant
            la semaine précédente sur cette échelle où <strong>0 représente une maladie inactive</strong> et <strong>10 une maladie très active</strong> ?
          </div>

          <div>
            <radio-toggle
              class="radio-group--inline"
              id="score_dapsa_activity_patient_done"
              name="score_dapsa_activity_patient_done"
              label="EVA patient réalisée ?"
              v-model="evaActivityDone"
              @input="resetEvaActivity"
            ></radio-toggle>

            <RangeInput
              v-if="parseInt(evaActivityDone)"
              id="activity_patient"
              label="<strong>EVA activité patient</strong>"
              min="0"
              max="10"
              before="Maladie inactive"
              after="Maladie très active"
              data-display-range="true"
              required
              v-bind="$attrs"
              v-model="eva.activity_patient"
              @input="emitScore"
            />
          </div>

          <div>
            Comment décrirez-vous l’intensité de vos douleurs articulaires durant la semaine précedente. Son extrémité gauche est marquée « pas de douleur ». <br />Elle est reliée par un trait à
            l’extrémité droite marquée « douleur maximale imaginable ».
          </div>

          <div>
            <radio-toggle
              class="radio-group--inline"
              id="score_dapsa_pain_patient_done"
              name="score_dapsa_pain_patient_done"
              label="EVA douleur réalisée ?"
              v-model="evaPainDone"
              @input="resetEvaPain"
            ></radio-toggle>

            <RangeInput
              v-if="parseInt(evaPainDone)"
              id="pain_patient"
              label="<strong>EVA douleur patient</strong>"
              min="0"
              max="10"
              before="Pas de douleur"
              after="Douleur maximale"
              data-display-range="true"
              required
              v-bind="$attrs"
              v-model="eva.pain_patient"
              @input="emitScore"
            />
          </div>

          <div>
            <radio-group
              class="radio-group--inline"
              :radios="psoriasis_area_options"
              label="<strong>Surface corporelle atteinte par le psoriasis</strong> (sachant que 1% correspond à la surface de la main doigts inclus) :"
              id="psoriasis_area"
              name="psoriasis_area"
              v-model="eva.psoriasis_area"
              v-bind="$attrs"
              required
              @input="emitScore"
            ></radio-group>
          </div>
        </div>
      </section>

      <div class="flex-row flex-gap-large">
        <div class="flex-item--100 flex-row flex-gap-large">
          <!-- Display results -->
          <div class="alert-success flex-item--100">
            <ul>
              <li>
                <span class="label">Articulations douloureuses :</span> <strong>{{ painfulJointsTotal }}</strong>
              </li>
              <li>
                <span class="label">Articulations gonflées :</span> <strong>{{ swollenJointsTotal }}</strong>
              </li>
              <li>
                <span class="label">DAPSA CRP :</span>&nbsp;
                <template v-if="!isNaN(scoreCRP)"
                  ><strong>{{ scoreCRP }}</strong> <span v-if="diseaseActivity">({{ diseaseActivity }})</span></template
                >
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DasSelect from "./DasSelect.vue";
import RangeInput from "./RangeInput.vue";
import radioGroup from "@/components/radioGroup.vue";
import radioToggle from "@/components/radioToggle.vue";

export default {
  name: "ScoreDapsa",
  inheritAttrs: false,
  components: {
    DasSelect,
    RangeInput,
    radioGroup,
    radioToggle,
  },
  props: ["value", "session"],

  data() {
    return {
      scoreDone: "0",
      crpDone: "0",
      evaActivityDone: "0",
      evaPainDone: "0",

      painfulJoints: {
        checkedDots: [],
      },
      swollenJoints: {
        checkedDots: [],
      },
      crp: "",
      eva: {
        activity_patient: "",
        pain_patient: "",
        psoriasis_area: "",
      },
      psoriasis_area_options: [
        { value: "Non fait" },
        { value: "0 %" },
        { value: "De 1 % à 9 %" },
        { value: "De 10 % à 29 %" },
        { value: "De 30 % à 49 %" },
        { value: "De 50 % à 69 %" },
        { value: "De 70 % à 89 %" },
        { value: "90 % à 100 %" },
      ],
    };
  },

  computed: {
    hasScore() {
      return parseInt(this.scoreDone);
    },

    // Disease Activity: scoreCRP = 0-4 Remission, 5-14 low, 15-28 moderate, >28 high Disease Activity
    diseaseActivity() {
      if (!this.hasScore || !this.scoreCRP) return "DM";

      if (this.scoreCRP >= 0 && this.scoreCRP <= 4) {
        return "rémission";
      } else if (this.scoreCRP >= 5 && this.scoreCRP <= 14) {
        return "activité faible";
      } else if (this.scoreCRP >= 15 && this.scoreCRP < 28) {
        return "activité modérée";
      } else if (this.scoreCRP >= 28) {
        return "activité élevée";
      } else {
        return "";
      }
    },

    painfulJointsTotal() {
      if (!this?.painfulJoints?.checkedDots.length) return 0;
      return this.painfulJoints.checkedDots.length;
    },

    swollenJointsTotal() {
      if (!this?.swollenJoints?.checkedDots.length) return 0;
      return this.swollenJoints.checkedDots.length;
    },

    /**
     * DAPSA score = TenderJoints + SwollenJoints + CRP + Activity + Pain
     * https://rheuma.charite.de/fileadmin/user_upload/microsites/ohne_AZ/m_cc13/rheuma/Templates/DAPSA_ENG.pdf
     *
     */
    scoreCRP() {
      if (this?.crp == "" || this?.eva?.activity_patient == "" || this?.eva?.pain_patient == "") return "";

      const painfulJointsTotal = this.painfulJointsTotal || 0;
      const swollenJointsTotal = this.swollenJointsTotal || 0;
      const crp = this?.crp ? parseFloat(this.crp) : 0;
      const activity = this?.eva?.activity_patient ? parseFloat(this.eva.activity_patient) : 0;
      const pain = this?.eva?.pain_patient ? parseFloat(this.eva.pain_patient) : 0;
      const score = (painfulJointsTotal + swollenJointsTotal + crp + activity + pain).toFixed(2);
      return score;
    },

    results() {
      return {
        score: `${this.scoreCRP}`, // raw score always as string
        scoreRendered: `DAPSA CRP : ${this.scoreCRP}`, // formatted score, ex. with unit 24%, 12mm, …
        values: {
          scoreDone: this.scoreDone.toString(),
          crpDone: this.crpDone,
          evaActivityDone: this.evaActivityDone,
          evaPainDone: this.evaPainDone,

          painfulJoints: this.painfulJoints.checkedDots,
          swollenJoints: this.swollenJoints.checkedDots,
          painfulJointsTotal: this.painfulJointsTotal,
          swollenJointsTotal: this.swollenJointsTotal,
          crp: this.crp,
          eva: this.eva,
          score_crp: this.scoreCRP,
        },
        details: null,
        detailsRendered: "",
      };
    },
  },

  methods: {
    emitScore() {
      this.$emit("input", this.results);
    },

    resetCrp() {
      if (!parseInt(this.crpDone)) {
        this.crp = "";
      }
    },

    resetEvaActivity() {
      if (!parseInt(this.evaActivityDone)) {
        this.eva.activity_patient = "";
      }
    },

    resetEvaPain() {
      if (!parseInt(this.evaPainDone)) {
        this.eva.pain_patient = "";
      }
    },

    resetScore() {
      this.crp = "";
      this.crpDone = "0";
      this.evaActivityDone = "0";
      this.evaPainDone = "0";
      this.painfulJoints.checkedDots = [];
      this.swollenJoints.checkedDots = [];
      this.eva.psoriasis_area = "";

      if (!this.hasScore) {
        this.scoreDone = "0";
        this.eva.activity_patient = "";
        this.eva.pain_patient = "";
      } else {
        this.scoreDone = "1";
        this.eva.activity_patient = 0;
        this.eva.pain_patient = 0;
      }

      this.emitScore();
    },
  },

  watch: {
    // Init data values from component props ("values")
    value: {
      immediate: true,
      handler: function(data) {
        this.scoreDone = data?.values?.scoreDone?.toString() || "0";
        this.crpDone = data?.values?.crpDone || "0";
        this.evaActivityDone = data?.values?.evaActivityDone || "0";
        this.evaPainDone = data?.values?.evaPainDone || "0";

        this.painfulJoints.checkedDots = data?.values?.painfulJoints || [];
        this.swollenJoints.checkedDots = data?.values?.swollenJoints || [];
        this.crp = data?.values?.crp || 0;
        if (data?.values?.eva) this.$set(this, "eva", { ...data?.values?.eva });
      },
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .radio-group--psoriasis_area.radio-group--inline {
    .label {
      flex-basis: 100%;
    }
  }
}
</style>
